import { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, FormControl, InputLabel, Select, MenuItem, LinearProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { uploadDocument } from '../../services/documents';
import type { DocumentFolder } from '../../types';

interface Props {
  open: boolean;
  folders: DocumentFolder[];
  onClose: () => void;
  onUploaded: () => void;
}

export default function UploadDocumentDialog({ open, folders, onClose, onUploaded }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [folderId, setFolderId] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    setFiles(Array.from(e.dataTransfer.files));
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) await uploadDocument(f, folderId || null);
      onUploaded();
      setFiles([]);
      setFolderId('');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Documents</DialogTitle>
      <DialogContent>
        {uploading && <LinearProgress sx={{ mb: 2 }} />}
        <Box
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          sx={{
            border: 2, borderStyle: 'dashed', borderColor: dragOver ? 'primary.main' : 'divider',
            borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer', mb: 2,
            bgcolor: dragOver ? 'action.hover' : 'background.paper',
          }}
        >
          <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography>{files.length ? files.map(f => f.name).join(', ') : 'Click or drag files here'}</Typography>
          <input ref={inputRef} type="file" multiple hidden onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
        </Box>
        <FormControl fullWidth size="small">
          <InputLabel>Folder (optional)</InputLabel>
          <Select value={folderId} label="Folder (optional)" onChange={(e) => setFolderId(e.target.value)}>
            <MenuItem value=""><em>None</em></MenuItem>
            {folders.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleUpload} disabled={!files.length || uploading}>Upload</Button>
      </DialogActions>
    </Dialog>
  );
}
